import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class MacFbtBundle extends HTMLElement {
  #button;
  #status;
  #checks;

  connectedCallback() {
    this.#button = this.querySelector('[data-fbt-add]');
    this.#status = this.querySelector('[data-fbt-status]');
    this.#checks = Array.from(this.querySelectorAll('[data-fbt-check]'));

    this.#checks.forEach((input) => input.addEventListener('change', () => this.#sync()));
    this.#button?.addEventListener('click', () => this.#addSelected());
    this.#sync();
  }

  #sync() {
    let selected = 0;
    this.#checks.forEach((input) => {
      const item = input.closest('[data-fbt-item]');
      item?.classList.toggle('is-selected', input.checked);
      if (input.checked) selected += 1;
    });

    const count = this.querySelector('[data-fbt-count]');
    if (count) count.textContent = String(selected);
    if (this.#button) this.#button.disabled = selected === 0;
  }

  async #addSelected() {
    const selected = this.#checks.filter((input) => input.checked);
    if (!selected.length || !this.#button) return;

    const items = selected.map((input) => ({
      id: Number(input.dataset.variantId),
      quantity: 1,
    }));

    const cartComponents = Array.from(document.querySelectorAll('cart-items-component'));
    const sectionIds = cartComponents
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
      .filter(Boolean);

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferredEventPromise.promise,
      })
    );

    const originalText = this.querySelector('[data-fbt-button-text]')?.textContent || 'Add selected to cart';
    const text = this.querySelector('[data-fbt-button-text]');
    this.#button.disabled = true;
    this.#button.classList.add('is-loading');
    if (text) text.textContent = 'Adding…';
    if (this.#status) this.#status.textContent = '';

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Could not add bundle to cart.');
      }

      const cartResponse = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'mac-fbt-bundle',
          sourceId: this.id,
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });

      if (text) text.textContent = 'Added to cart ✓';
      if (this.#status) this.#status.textContent = `${items.length} item${items.length > 1 ? 's' : ''} added to your cart.`;
      setTimeout(() => {
        if (text) text.textContent = originalText;
      }, 1800);
    } catch (error) {
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Could not add bundle to cart.',
          code: 'INVALID',
        })
      );
      if (text) text.textContent = originalText;
      if (this.#status) this.#status.textContent = error?.message || 'Could not add bundle to cart.';
    } finally {
      this.#button.classList.remove('is-loading');
      this.#button.disabled = this.#checks.every((input) => !input.checked);
    }
  }
}

if (!customElements.get('mac-fbt-bundle')) {
  customElements.define('mac-fbt-bundle', MacFbtBundle);
}
